import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Premium SVG/CSS notebook mockup with a dark SaaS dashboard inside.
 * Uses 3D CSS transforms + Framer Motion for subtle floating animation.
 * Dashboard text is fully localised through the language context.
 */
const NotebookMockup = () => {
  return (
    <div className="notebook-perspective relative w-full max-w-[640px] mx-auto" data-testid="notebook-mockup">
      <div
        aria-hidden
        className="absolute -inset-10 glow-radial opacity-80 pointer-events-none"
      />

      <motion.div
        className="notebook-tilt relative"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div
            className="relative rounded-[18px] bg-[#0b1220] border border-[#1dc7e9]/15 overflow-hidden"
            style={{
              boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(29,199,233,0.08), 0 0 60px rgba(29,199,233,0.18)",
            }}
          >
            <div className="h-5 flex items-center justify-center bg-black">
              <div className="w-2 h-2 rounded-full bg-[#1dc7e9]/40" />
            </div>

            <DashboardContent />
          </div>

          <div
            className="mx-auto h-3 w-[103%] -mt-px rounded-b-[18px] relative"
            style={{
              background:
                "linear-gradient(180deg, #0b1220 0%, #0f1a2c 50%, #050816 100%)",
              transform: "translateZ(-2px)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.55)",
            }}
          >
            <div className="absolute inset-x-[42%] top-0 h-1.5 rounded-b-full bg-black/40" />
          </div>
        </motion.div>

        <Particles />
      </motion.div>
    </div>
  );
};

const DashboardContent = () => {
  const { t } = useLang();
  const d = t.hero.dashboard;

  return (
    <div className="bg-[#050a18] p-4 sm:p-5 min-h-[360px] relative">
      <div aria-hidden className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
      <div aria-hidden className="absolute -inset-32 glow-radial opacity-30 pointer-events-none" />

      <div className="relative flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]/80" />
        </div>
        <div className="text-[10px] mono text-[#b8c0cc]/60 truncate">{d.url}</div>
        <div className="text-[10px] mono text-[#1dc7e9] flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1dc7e9] animate-pulse" />
          {d.live}
        </div>
      </div>

      <div className="relative grid grid-cols-3 gap-2 mb-4">
        <KPI label={d.kpiDeliveries} value="42.8k" trend="+12.4%" />
        <KPI label={d.kpiReadRate} value="96.7%" trend="+3.1%" />
        <KPI label={d.kpiChannels} value="5/5" trend="OK" mono />
      </div>

      <div className="relative rounded-lg bg-[#0b1220]/70 border border-[#1dc7e9]/10 p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] text-white font-semibold">{d.chartTitle}</div>
          <div className="text-[10px] mono text-[#b8c0cc]/70">{d.chartRange}</div>
        </div>
        <DeliveryChart />
      </div>

      <div className="relative grid grid-cols-5 gap-3">
        <div className="col-span-3 rounded-lg bg-[#0b1220]/70 border border-[#1dc7e9]/10 p-3">
          <div className="text-[11px] text-white font-semibold mb-2">{d.recentDocs}</div>
          <DocList d={d} />
        </div>
        <div className="col-span-2 rounded-lg bg-[#0b1220]/70 border border-[#1dc7e9]/10 p-3">
          <div className="text-[11px] text-white font-semibold mb-2">{d.channelMix}</div>
          <ChannelMix d={d} />
        </div>
      </div>
    </div>
  );
};

const KPI = ({ label, value, trend, mono }) => (
  <div className="rounded-md bg-[#0b1220]/70 border border-[#1dc7e9]/10 px-3 py-2">
    <div className="text-[9px] uppercase tracking-widest text-[#b8c0cc]/70">{label}</div>
    <div className={`text-base font-bold text-white ${mono ? "mono" : ""}`}>{value}</div>
    <div className="text-[9px] mono text-[#1dc7e9]">{trend}</div>
  </div>
);

const DeliveryChart = () => {
  const pts = [
    8, 12, 10, 18, 22, 16, 20, 28, 26, 32, 30, 36, 44, 38, 48, 54, 50, 58, 62,
    56, 64, 72, 68, 80,
  ];
  const max = Math.max(...pts);
  const points = pts
    .map((p, i) => {
      const x = (i / (pts.length - 1)) * 100;
      const y = 100 - (p / max) * 100;
      return `${x},${y}`;
    })
    .join(" ");
  const areaPoints = `0,100 ${points} 100,100`;

  return (
    <svg viewBox="0 0 100 100" className="w-full h-20" preserveAspectRatio="none">
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1dc7e9" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#1dc7e9" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#g1)" />
      <polyline
        points={points}
        fill="none"
        stroke="#1dc7e9"
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={100}
        cy={100 - (pts[pts.length - 1] / max) * 100}
        r="1.4"
        fill="#1dc7e9"
      >
        <animate attributeName="r" values="1.2;2.4;1.2" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

const DocList = ({ d }) => {
  const rows = [
    { id: "DOC-8821", type: d.types.holerite, ch: d.channels.whatsapp, ar: d.ar, status: "ok" },
    { id: "DOC-8820", type: d.types.fatura, ch: d.channels.rcs, ar: d.ar, status: "ok" },
    { id: "DOC-8819", type: d.types.oficial, ch: d.channels.email, ar: d.pending, status: "wait" },
    { id: "DOC-8818", type: d.types.contrato, ch: d.channels.whatsapp, ar: d.ar, status: "ok" },
  ];
  return (
    <div className="w-full">
      {/* Header row */}
      <div className="grid grid-cols-[1.1fr_1fr_1fr_0.9fr] gap-2 pb-1.5 border-b border-white/5 text-[8.5px] mono uppercase tracking-[0.18em] text-[#b8c0cc]/60">
        <span>ID</span>
        <span>Tipo</span>
        <span>Canal</span>
        <span>Status</span>
      </div>
      {/* Rows */}
      <div className="flex flex-col">
        {rows.map((r) => (
          <div
            key={r.id}
            className="grid grid-cols-[1.1fr_1fr_1fr_0.9fr] gap-2 items-center py-1.5 border-b border-white/5 last:border-b-0 text-[10px] mono"
          >
            <span className="text-[#1dc7e9] truncate">{r.id}</span>
            <span className="text-white/85 truncate">{r.type}</span>
            <span className="text-[#b8c0cc] truncate">{r.ch}</span>
            <span
              className={`flex items-center gap-1.5 truncate ${
                r.status === "ok" ? "text-emerald-400" : "text-amber-300"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  r.status === "ok" ? "bg-emerald-400" : "bg-amber-300"
                }`}
              />
              {r.ar}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChannelMix = ({ d }) => {
  const channels = [
    { label: d.channels.whatsapp, pct: 48 },
    { label: d.channels.email, pct: 22 },
    { label: d.channels.sms, pct: 14 },
    { label: d.channels.rcs, pct: 10 },
    { label: d.channels.voice, pct: 6 },
  ];
  return (
    <div className="flex flex-col gap-1.5">
      {channels.map((c) => (
        <div key={c.label}>
          <div className="flex items-center justify-between text-[10px] text-[#b8c0cc] mb-0.5">
            <span>{c.label}</span>
            <span className="mono text-white/80">{c.pct}%</span>
          </div>
          <div className="h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#1dc7e9]/80"
              style={{ width: `${c.pct}%`, boxShadow: "0 0 12px rgba(29,199,233,0.5)" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const Particles = () => {
  const dots = Array.from({ length: 18 });
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      {dots.map((_, i) => {
        const size = 2 + (i % 4);
        const left = (i * 53) % 100;
        const top = (i * 37) % 100;
        const delay = (i % 7) * 0.4;
        const duration = 5 + (i % 6);
        return (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#1dc7e9]"
            style={{
              width: size,
              height: size,
              left: `${left}%`,
              top: `${top}%`,
              filter: "blur(0.5px)",
              boxShadow: "0 0 10px rgba(29,199,233,0.7)",
            }}
            animate={{ y: [0, -20, 0], opacity: [0.15, 0.65, 0.15] }}
            transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
          />
        );
      })}
    </div>
  );
};

export default NotebookMockup;
